import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ip_back } from '../config';
import UserTypeSelection from './UserTypeSelection';
import QuickProfileQuestions from './QuickProfileQuestions';
import { LegalAcceptCheckbox } from './legal/LegalAcceptCheckbox';
import { recordPrivacyAndTermsAcceptance } from '../api/socialClipApi';
import './RegistrationFlow.css';

const RegistrationFlow = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [currentStep, setCurrentStep] = useState('userType'); // userType, quickProfile, password
  const [userType, setUserType] = useState(null);
  const [quickProfileData, setQuickProfileData] = useState(null);
  const [passwordData, setPasswordData] = useState({
    password: '',
    passwordConfirmation: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    console.log('🔍 RegistrationFlow: Checking email confirmation token...');
    
    // Check URL parameters for token (from email confirmation link)
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    
    // Check if email confirmation token exists in localStorage or URL
    const emailConfirmationToken = localStorage.getItem('emailConfirmationToken') || urlToken;
    const pendingEmail = localStorage.getItem('pendingEmail');
    const isPasswordRegistration = location.pathname === '/password-registration';
    
    console.log('🔍 Token exists:', !!emailConfirmationToken);
    console.log('🔍 Pending email:', pendingEmail);
    console.log('🔍 Is password registration:', isPasswordRegistration);
    
    // If this is password registration route, check for existing registration data
    if (isPasswordRegistration) {
      const registrationData = localStorage.getItem('registrationData');
      if (registrationData) {
        try {
          const data = JSON.parse(registrationData);
          if (data.userType && data.quickProfileData) {
            // This is for admin/coach - go to password step
            setUserType(data.userType);
            setQuickProfileData(data.quickProfileData);
            setCurrentStep('password');
            setEmailConfirmed(true);
            console.log('✅ Password registration for admin/coach detected');
            return;
          }
        } catch (error) {
          console.error('Error parsing registration data:', error);
        }
      }
      // If no registration data, check if this is a player coming from profiling
      const selectedUserType = localStorage.getItem('selectedUserType');
      const profilingData = localStorage.getItem('profilingFormData');
      
      if (selectedUserType === 'player') {
        if (profilingData) {
          // Player has completed profiling, go to password step
          try {
            const data = JSON.parse(profilingData);
            setUserType('player');
            setCurrentStep('password');
            setEmailConfirmed(true);
            console.log('✅ Player coming from profiling - going to password step');
            return;
          } catch (error) {
            console.error('Error parsing profiling data:', error);
          }
        }
        // Player hasn't completed profiling yet, redirect to profiling
        navigate('/profiling');
        return;
      }
    }
    
    // If no token, this is a direct registration (start with user type selection)
    if (!emailConfirmationToken) {
      console.log('🆕 Direct registration - starting with user type selection');
      setCurrentStep('userType');
      setEmailConfirmed(true);
      return;
    }
    
    // If we have a token but no pending email, try to decode it
    if (emailConfirmationToken && !pendingEmail) {
      try {
        const tokenParts = emailConfirmationToken.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          if (payload.email) {
            localStorage.setItem('pendingEmail', payload.email);
            setUserEmail(payload.email);
            console.log('✅ Extracted email from token:', payload.email);
          }
        }
      } catch (error) {
        console.log('❌ Could not decode token:', error);
        // If token is invalid, start fresh registration
        setCurrentStep('userType');
        setEmailConfirmed(true);
        return;
      }
    } else if (pendingEmail) {
      setUserEmail(pendingEmail);
      console.log('✅ Using stored email:', pendingEmail);
    }
    
    setEmailConfirmed(true);
    console.log('✅ Email confirmation validated successfully');
  }, [navigate, location.pathname]);

  const handleUserTypeSelect = (selectedUserType) => {
    console.log('🎯 User type selected:', selectedUserType);
    setUserType(selectedUserType);
    
    if (selectedUserType === 'player') {
      localStorage.setItem('selectedUserType', selectedUserType);
      navigate('/profiling');
    } else if (selectedUserType === 'club_admin') {
      // For club admin, redirect to club admin profiling
      localStorage.setItem('selectedUserType', selectedUserType);
      navigate('/club-admin-profiling');
    } else if (selectedUserType === 'parent') {
      // For parent, redirect to parent conversational profiling wizard
      localStorage.setItem('selectedUserType', selectedUserType);
      navigate('/parent-profiling');
    } else {
      // For coach, use quick profile questions
      setCurrentStep('quickProfile');
    }
  };

  const handleQuickProfileComplete = (profileData) => {
    console.log('📋 Quick profile completed:', profileData);
    setQuickProfileData(profileData);
    
    // Quick profile is only for club admin and coach, go directly to password
    setCurrentStep('password');
  };


  const createPlayerWithProfile = async (profileData, passwordData) => {
    console.log('🎯 Creating player with profile data...');
    
    // For now, we'll need to get email from somewhere - let's use a default or ask user
    // This is a limitation of the current flow - we need email for user creation
    const email = localStorage.getItem('pendingEmail') || 'player@example.com';
    
    const registerData = {
      email: email,
      password: passwordData.password,
      firstName: profileData.firstName || email.split('@')[0],
      lastName: profileData.lastName || 'User',
      sex: profileData.gender || 'male',
      dateBorn: profileData.dateOfBirth || new Date('1990-01-01'),
      typeUser_id: 4, // Player (1=SuperAdmin, 2=Admin, 3=Coach, 4=Player)
      confirmation_token: 'direct_registration' // Special token for direct registration
    };
    
    console.log('📤 Creating user:', registerData);
    let newUser;
    try {
      const registerResponse = await axios.post(`${ip_back}api/user/registerUser`, registerData);
      if (registerResponse.data.status !== 'success') {
        throw new Error(registerResponse.data.message || 'Failed to create user');
      }
      newUser = registerResponse.data.data;
    } catch (registerError) {
      const errMsg =
        registerError?.response?.data?.message || registerError?.message || '';
      if (!/already registered/i.test(errMsg)) throw registerError;
      console.log('👤 Account already exists — signing in to finish profile');
      const loginResponse = await axios.post(`${ip_back}api/user/login`, {
        email,
        password: passwordData.password,
      });
      if (loginResponse.data.status !== 'success' || !loginResponse.data.data) {
        throw new Error(
          'This email is already registered. Please log in with your password to finish your profile.'
        );
      }
      newUser = loginResponse.data.data;
    }
    console.log('✅ User created:', newUser);
    
    // Save user data
    localStorage.setItem('user', JSON.stringify(newUser));
    localStorage.setItem('token', newUser.user_token);
    await recordPrivacyAndTermsAcceptance();
    try {
      localStorage.setItem('home_active_role', 'player');
    } catch {
      /* ignore */
    }
    
    // Complete the profile
    const profileCompletionData = {
      userId: newUser.user_id,
      mainPurposeId: profileData.mainPurposeId,
      experienceLevelId: profileData.experienceLevelId,
      styleId: profileData.styleId,
      primaryFocusId: profileData.primaryFocusId,
      secondaryFocusIds: profileData.secondaryFocusIds,
      primaryFocusCustomText: profileData.primaryFocusCustomText,
      secondaryFocusCustomText: profileData.secondaryFocusCustomText,
      levelId: profileData.levelId,
      customLevel: profileData.customLevel,
      clubId: profileData.clubId,
      grip: profileData.grip,
      hand: profileData.hand,
      gender: profileData.gender,
      dateOfBirth: profileData.dateOfBirth,
      firstName: profileData.firstName,
      lastName: profileData.lastName
    };
    
    console.log('📤 Completing profile:', profileCompletionData);
    const profileResponse = await axios.post(`${ip_back}api/profile/complete`, profileCompletionData);
    
    if (profileResponse.data.success) {
      console.log('✅ Profile completed successfully');
      // Clean up localStorage
      localStorage.removeItem('profilingFormData');
      localStorage.removeItem('selectedUserType');
      // Redirect to success page or dashboard
      navigate('/dashboard');
    } else {
      throw new Error('Failed to complete profile');
    }
  };

  const handlePasswordSubmit = async (passwordData) => {
    console.log('🔐 Password submitted, completing registration...');
    setPasswordData(passwordData);
    
    setIsLoading(true);
    
    try {
      // Get the email confirmation token
      const emailConfirmationToken = localStorage.getItem('emailConfirmationToken');
      const email = localStorage.getItem('pendingEmail');
      
      // For direct registration (no email confirmation), handle based on user type
      if (!emailConfirmationToken || !email) {
        console.log('🆕 Direct registration - handling based on user type');
        
        if (userType === 'player') {
          // Player with profiling data - create user and complete profile
          const profilingData = localStorage.getItem('profilingFormData');
          if (profilingData) {
            try {
              const profileData = JSON.parse(profilingData);
              await createPlayerWithProfile(profileData, passwordData);
              return;
            } catch (error) {
              console.error('Error creating player with profile:', error);
              throw error;
            }
          } else {
            // No profiling data - redirect to profiling
            const collectedData = {
              userType,
              quickProfileData
            };
            localStorage.setItem('registrationData', JSON.stringify(collectedData));
            navigate('/profiling');
            return;
          }
        } else {
          // Admin/coach - redirect to old flow
          const collectedData = {
            userType,
            quickProfileData
          };
          localStorage.setItem('registrationData', JSON.stringify(collectedData));
          navigate('/profiling');
          return;
        }
      }
      
      // Determine user type ID based on selected type
      let typeUser_id;
      switch (userType) {
        case 'club_admin':
          typeUser_id = 2; // Admin
          break;
        case 'coach':
          typeUser_id = 3; // Coach
          break;
        case 'player':
          typeUser_id = 4; // Player
          break;
        default:
          typeUser_id = 4; // Default to player
      }
      
      // Create user with password
      let firstName, lastName, sex, dateBorn;
      
      if (userType === 'player') {
        // For players, get data from profiling form
        const profilingData = localStorage.getItem('profilingFormData');
        if (profilingData) {
          try {
            const profileData = JSON.parse(profilingData);
            firstName = profileData.firstName || email.split('@')[0];
            lastName = profileData.lastName || 'User';
            sex = profileData.gender || 'male';
            dateBorn = profileData.dateOfBirth ? new Date(profileData.dateOfBirth) : new Date('1990-01-01');
          } catch (error) {
            console.error('Error parsing profiling data:', error);
            // Fallback to default values
            firstName = email.split('@')[0];
            lastName = 'User';
            sex = 'male';
            dateBorn = new Date('1990-01-01');
          }
        } else {
          // Fallback if no profiling data
          firstName = email.split('@')[0];
          lastName = 'User';
          sex = 'male';
          dateBorn = new Date('1990-01-01');
        }
      } else {
        // For admin/coach, use quickProfileData
        firstName = quickProfileData?.firstName || email.split('@')[0];
        lastName = quickProfileData?.lastName || 'User';
        sex = quickProfileData?.gender || 'male';
        dateBorn = new Date('1990-01-01'); // Default date
      }
      
      const registerData = {
        email: email,
        password: passwordData.password,
        firstName: firstName,
        lastName: lastName,
        sex: sex,
        dateBorn: dateBorn,
        typeUser_id: typeUser_id,
        confirmation_token: emailConfirmationToken
      };
      
      let newUser;
      try {
        const registerResponse = await axios.post(`${ip_back}api/user/registerUser`, registerData);
        if (registerResponse.data.status !== 'success') {
          throw new Error(registerResponse.data.message || 'Registration failed');
        }
        newUser = registerResponse.data.data;
      } catch (registerError) {
        const errMsg =
          registerError?.response?.data?.message || registerError?.message || '';
        if (!/already registered/i.test(errMsg) || Number(typeUser_id) !== 4) {
          throw registerError;
        }
        console.log('👤 Account already exists — signing in to continue');
        const loginResponse = await axios.post(`${ip_back}api/user/login`, {
          email,
          password: passwordData.password,
        });
        if (loginResponse.data.status !== 'success' || !loginResponse.data.data) {
          throw new Error(
            'This email is already registered. Please log in with your password.'
          );
        }
        newUser = loginResponse.data.data;
      }
      
      // Save user data
      localStorage.setItem('user', JSON.stringify(newUser));
      localStorage.setItem('token', newUser.user_token);
      await recordPrivacyAndTermsAcceptance();
      
      // Clean up temporary data
      localStorage.removeItem('emailConfirmationToken');
      localStorage.removeItem('pendingEmail');
      
      // For club admin and coach, complete basic profile
      if (userType !== 'player') {
        const basicProfileData = {
          userId: newUser.user_id,
          clubName: quickProfileData.club,
          phone: quickProfileData.phone,
          specialization: quickProfileData.specialization || null,
          experience: quickProfileData.experience || null
        };
        
        // TODO: Create API endpoint for basic profile completion for admin/coach
        console.log('📋 Basic profile data for admin/coach:', basicProfileData);
      }
      // For players, the profiling will be completed separately on the profiling page
      
      // Redirect to appropriate dashboard
      if (newUser.type_user_id === 1) {
        console.log('👑 Redirecting admin to /Dashboard');
        navigate('/Dashboard');
      } else {
        console.log('🎾 Redirecting user to /home');
        navigate('/home');
      }
      
    } catch (error) {
      console.error('Registration error:', error);
      
      let errorMessage = 'Registration failed. Please try again.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setErrors({ 
        general: errorMessage
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    switch (currentStep) {
      case 'quickProfile':
        setCurrentStep('userType');
        break;
      case 'password':
        setCurrentStep('quickProfile');
        break;
      default:
        break;
    }
  };

  if (!emailConfirmed) {
    return (
      <div className="profiling-container">
        <div className="profiling-card">
          <div className="profiling-header">
            <div className="profiling-logo">
              <div className="loading-spinner"></div>
            </div>
            <h1>{t('verifyingEmail')}</h1>
            <p>{t('pleaseWaitVerifyEmail')}</p>
            <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '1rem' }}>
              {t('ifTakesTooLong')}
            </p>
            <button 
              onClick={() => navigate('/register')} 
              className="next-button"
            >
              {t('backToRegistration')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="registration-flow-container">
      {currentStep === 'userType' && (
        <UserTypeSelection 
          onUserTypeSelect={handleUserTypeSelect}
        />
      )}

      {currentStep === 'quickProfile' && (
        <QuickProfileQuestions 
          userType={userType}
          onComplete={handleQuickProfileComplete}
          onBack={handleBack}
        />
      )}
      
      
      {currentStep === 'password' && (
        <PasswordStep 
          userEmail={userEmail}
          onSubmit={handlePasswordSubmit}
          onBack={handleBack}
          errors={errors}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

// Password Step Component
const PasswordStep = ({ userEmail, onSubmit, onBack, errors, isLoading }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    password: '',
    passwordConfirmation: '',
    legalAccepted: false
  });
  const [localErrors, setLocalErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (localErrors[name]) {
      setLocalErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.password) {
      newErrors.password = t('passwordRequired');
    } else if (formData.password.length < 6) {
      newErrors.password = t('passwordMinLength');
    }
    
    if (!formData.passwordConfirmation) {
      newErrors.passwordConfirmation = t('passwordConfirmationRequired');
    } else     if (formData.password !== formData.passwordConfirmation) {
      newErrors.passwordConfirmation = t('passwordsDoNotMatch');
    }

    if (!formData.legalAccepted) {
      newErrors.legalAccepted = t('legalAcceptRequired', 'Please confirm you have read the privacy notice and accept the terms of use');
    }
    
    setLocalErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <div className="profiling-container">
      <div className="profiling-card">
        <div className="profiling-header">
          <div className="profiling-logo">
            <div className="success-icon">✅</div>
          </div>
          <h1>{t('emailConfirmed')}</h1>
          <p>{t('welcomeCreatePassword')}</p>
          {userEmail && <p className="email-display">{t('emailDisplay')} {userEmail}</p>}
        </div>
        
        <form onSubmit={handleSubmit} className="profiling-form">
          {errors.general && (
            <div className="error-message general-error">
              {errors.general}
            </div>
          )}
          
          <div className="input-field">
            <label htmlFor="password">{t('password')}</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={localErrors.password ? 'error' : ''}
              placeholder={t('enterPassword')}
            />
            {localErrors.password && (
              <span className="error-message">{localErrors.password}</span>
            )}
          </div>
          
          <div className="input-field">
            <label htmlFor="passwordConfirmation">{t('confirmPassword')}</label>
            <input
              type="password"
              id="passwordConfirmation"
              name="passwordConfirmation"
              value={formData.passwordConfirmation}
              onChange={handleInputChange}
              className={localErrors.passwordConfirmation ? 'error' : ''}
              placeholder={t('confirmPasswordPlaceholder')}
            />
            {localErrors.passwordConfirmation && (
              <span className="error-message">{localErrors.passwordConfirmation}</span>
            )}
          </div>

          <LegalAcceptCheckbox
            checked={formData.legalAccepted}
            onChange={(v) => setFormData((prev) => ({ ...prev, legalAccepted: v }))}
          />
          {localErrors.legalAccepted && (
            <span className="error-message">{localErrors.legalAccepted}</span>
          )}
          
          <div className="profiling-actions">
            {onBack && (
              <button 
                type="button" 
                className="back-button"
                onClick={onBack}
              >
                {t('back')}
              </button>
            )}
            <button
              type="submit"
              className="next-button"
              disabled={isLoading}
            >
              {isLoading ? t('creatingAccount') : t('createAccount')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegistrationFlow;
